import type { Lang, Localized, OpeningHours } from "./types";

export const loc = (value: Localized | undefined, lang: Lang): string =>
  value ? (lang === "ar" ? value.ar : value.en) || value.en || value.ar : "";

/** Always Latin (Western) digits + Gregorian calendar, even in Arabic. */
const localeFor = (lang: Lang) =>
  lang === "ar" ? "ar-EG-u-nu-latn-ca-gregory" : "en-GB";

export function formatNumber(value: number, lang: Lang): string {
  return new Intl.NumberFormat(localeFor(lang), { maximumFractionDigits: 2 }).format(value);
}

let currencyCode = "USD";

export function configureMoney(currency: string): void {
  currencyCode = currency.toUpperCase();
}

export function formatPrice(amount: number, lang: Lang, currency = currencyCode): string {
  const formatted = new Intl.NumberFormat(localeFor(lang), {
    style: "currency",
    currency,
    currencyDisplay: lang === "ar" ? "symbol" : "narrowSymbol",
  }).format(amount / 100);
  return `\u2068${formatted}\u2069`;
}

export function formatDate(iso: string, lang: Lang, withTime = false): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return new Intl.DateTimeFormat(localeFor(lang), {
    year: "numeric",
    month: "long",
    day: "numeric",
    ...(withTime ? { hour: "2-digit", minute: "2-digit", hour12: false } : {}),
  }).format(d);
}

export function formatTime(hhmm: string, lang: Lang): string {
  const [h, m] = hhmm.split(":").map(Number);
  const d = new Date();
  d.setHours(h ?? 0, m ?? 0, 0, 0);
  return new Intl.DateTimeFormat(localeFor(lang), {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(d);
}

export function formatHours(hours: OpeningHours, lang: Lang): string {
  if (hours.closed) return lang === "ar" ? "مغلق" : "Closed";
  return `\u2066${formatTime(hours.open, lang)} – ${formatTime(hours.close, lang)}\u2069`;
}

const toMinutes = (hhmm: string) => {
  const [h, m] = hhmm.split(":").map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
};

/** Supports overnight ranges such as 20:00 -> 02:00 */
export function isOpenAt(hours: OpeningHours, at: Date = new Date()): boolean {
  if (hours.closed) return false;
  const now = at.getHours() * 60 + at.getMinutes();
  const open = toMinutes(hours.open);
  const close = toMinutes(hours.close);
  if (open === close) return true;
  return open < close ? now >= open && now < close : now >= open || now < close;
}

/** A branch is usable only if it can still fulfil an order ~30 minutes from now. */
export function canFulfilSoon(hours: OpeningHours, minutesAhead = 30): boolean {
  const at = new Date(Date.now() + minutesAhead * 60_000);
  return isOpenAt(hours, at) && isOpenAt(hours);
}

const ARABIC_DIGITS = "٠١٢٣٤٥٦٧٨٩";

export function toLatinDigits(input: string): string {
  return input
    .replace(/[٠-٩]/g, (d) => String(ARABIC_DIGITS.indexOf(d)))
    .replace(/[۰-۹]/g, (d) => String("۰۱۲۳۴۵۶۷۸۹".indexOf(d)));
}

export function normalizeNumberEntry(input: string, decimal: boolean): string {
  const latin = toLatinDigits(input).replace(/\u066B/g, ".").replace(/\u066C/g, "");
  const sign = /^[+-]/.exec(latin)?.[0] ?? "";
  if (!decimal) return sign + latin.slice(sign.length).replace(/[^0-9]/g, "");

  const unsigned = latin.slice(sign.length).replace(/[^0-9.]/g, "");
  const decimalIndex = unsigned.indexOf(".");
  return sign + (decimalIndex < 0
    ? unsigned
    : unsigned.slice(0, decimalIndex + 1) + unsigned.slice(decimalIndex + 1).replace(/\./g, ""));
}

export function normalizeDigits(input: string): string {
  return toLatinDigits(input).replace(/\D/g, "");
}

export function isValidPhone(input: string): boolean {
  const digits = normalizeDigits(input);
  return /^\d{7,15}$/.test(digits);
}

export function normalizePhoneForMarket(input: string, countryCode: string): string {
  if (countryCode.toUpperCase() !== "EG") {
    return toLatinDigits(input.trim())
      .replace(/^00/, "+")
      .replace(/[\s().-]/g, "");
  }
  const digits = normalizeDigits(input);
  if (!digits) return "";
  const withoutCountryCode = digits.startsWith("0020")
    ? digits.slice(4)
    : digits.startsWith("20")
      ? digits.slice(2)
      : digits;
  return `0${withoutCountryCode.replace(/^0+/, "")}`;
}

export function isValidMarketPhone(input: string, countryCode: string): boolean {
  const normalized = normalizePhoneForMarket(input, countryCode);
  return countryCode.toUpperCase() === "EG"
    ? /^01[0125]\d{8}$/.test(normalized)
    : /^\+?\d{7,15}$/.test(normalized);
}

export function haversineKm(
  a: { lat: number; lng: number },
  b: { lat: number; lng: number },
): number {
  const R = 6371;
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLng = ((b.lng - a.lng) * Math.PI) / 180;
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((a.lat * Math.PI) / 180) * Math.cos((b.lat * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
}
